import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import _ from 'lodash';
import { ReceiptOwnerCode } from '../../../models/expense-receipt/expense-receipt';
import {
  ExpenseSettlement,
  ExpenseSettlementDirection,
  ExpenseSettlementOwner,
  ExpenseSettlementSearchCommand,
  ExpenseSettlementStatus,
} from '../../../models/expense-settlement/expense-settlement';
import { PettyCashRoles } from '../../../models/roles/roles-group';
import { ExpenseSettlementProxyService } from '../../../proxies/expense-settlement.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { Currency, currencySymbol } from '../../../utilities/currency.util';
import { CASH_DOCUMENT_STATUSES, cashDocumentStatusLabel, cashDocumentStatusSeverity } from '../cash-document-status.util';

/** The filter panel's controls; select values arrive as strings. */
interface ExpenseSettlementFilterFormModel {
  search: string | null;
  status: string | null;
  ownerCode: string | null;
}

type ExpenseSettlementFilters = Partial<ExpenseSettlementFilterFormModel>;

/**
 * Expense settlements (Liquidación, #5799): the treasurer's queue of funds and trips with posted claims still to pay,
 * and every settlement made. A settlement starts from the queue, or from a fund's or trip's page.
 */
@Component({
  selector: 'app-expense-settlements',
  templateUrl: './expense-settlements.component.html',
})
export class ExpenseSettlementsComponent {
  private readonly proxy = inject(ExpenseSettlementProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly userAuth = inject(UserAuthenticationService);

  private currentSearchCriteria: ExpenseSettlementSearchCommand = {};

  readonly canSettle = this.userAuth.roles.includes(PettyCashRoles.PettyCashTreasurer);
  readonly ownerCodes = ReceiptOwnerCode;
  readonly directions = ExpenseSettlementDirection;

  readonly isLoading = signal(0);
  readonly showFilters = signal(true);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'expense-settlement-filters', config: this.filterConfig });
  readonly items = signal<ExpenseSettlement[]>([]);
  readonly queue = signal<ExpenseSettlementOwner[]>([]);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal(-1);

  constructor() {
    this.filterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchWithFilters(filters as ExpenseSettlementFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
    this.loadQueue();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('ExpenseSettlement_Filter_Search', 'Number, fund or trip'),
          placeholder: this.t('ExpenseSettlement_Filter_Search_Placeholder', 'Search by number, fund or trip'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'ownerCode',
          label: this.t('ExpenseClaim_Filter_Type', 'Type'),
          placeholder: this.t('ExpenseClaim_Filter_Type_Placeholder', 'Fund or trip'),
          options: [
            { label: this.t('ExpenseClaim_Type_Fund', 'Petty cash fund'), value: String(ReceiptOwnerCode.PettyCashFund) },
            { label: this.t('ExpenseClaim_Type_Trip', 'Trip'), value: String(ReceiptOwnerCode.Trip) },
          ],
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: CASH_DOCUMENT_STATUSES.map((status) => ({ label: this.statusLabel(status), value: String(status) })),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private searchWithFilters(filters: ExpenseSettlementFilters): void {
    const search = (filters.search ?? '').toString().trim();
    const status = _.isNil(filters.status) || filters.status === '' ? null : (Number(filters.status) as ExpenseSettlementStatus);
    const ownerCode = _.isNil(filters.ownerCode) || filters.ownerCode === '' ? null : Number(filters.ownerCode);
    this.currentSearchCriteria = { search: search || null, statuses: status === null ? null : [status], ownerCode };
    this.currentPage.set(1);
    this.search();
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search();
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search();
  }

  private search(): void {
    this.startLoading();
    this.proxy
      .search(this.currentSearchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.items.set(response.data?.items ?? []);
        this.totalRecords.set(response.data?.totalCount ?? 0);
      });
  }

  private loadQueue(): void {
    this.proxy.getOwners().subscribe((response) => this.queue.set(response.data ?? []));
  }

  redirectToDetails(id: string): void {
    this.router.navigate(['/expense-settlement-details'], { queryParams: { id } });
  }

  createSettlement(): void {
    this.redirectToDetails('new');
  }

  /** From the queue: a new settlement for that fund or trip. */
  settle(owner: ExpenseSettlementOwner): void {
    this.router.navigate(['/expense-settlement-details'], {
      queryParams: { id: 'new', ownerReference: owner.owner.reference, ownerCode: owner.owner.entityDefinitionCode },
    });
  }

  statusLabel(status: ExpenseSettlementStatus | number): string {
    return cashDocumentStatusLabel(status, (k, f) => this.t(k, f));
  }

  statusSeverity(status: ExpenseSettlementStatus): 'success' | 'warn' | 'secondary' {
    return cashDocumentStatusSeverity(status);
  }

  symbolOf(currency: Currency): string {
    return currencySymbol(currency);
  }

  getRow(rowData: unknown): ExpenseSettlement {
    return rowData as ExpenseSettlement;
  }

  getQueueRow(rowData: unknown): ExpenseSettlementOwner {
    return rowData as ExpenseSettlementOwner;
  }
}
